import { formatHalloGreeting } from './mailGreeting';

const GOLD = '#DCB68A';
const DEEP = '#0A0A0A';
const SLATE = '#0F172A';
const CARD = '#1E293B';
const TEXT = '#F8FAFC';
const MUTED = '#94A3B8';

const DEFAULT_PEBASE = 'https://pebase.friseur-coaching.de/';
const DEFAULT_VIMEO = 'https://vimeo.com/1229244105?share=copy&fl=sv&fe=ci';

export type CoachAssignmentMailData = {
  clientName: string;
  salonName: string;
  assignedCoachName: string;
  finalMandantennummer: string;
  pebaseLink?: string;
  vimeoLink?: string;
};

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function firstNameFromClientName(clientName: string): string {
  const raw = String(clientName || '').trim();
  if (!raw) return '';
  return raw.split(/\s+/)[0] || '';
}

/** Noble-Slate HTML für Betreuer-Zuweisung + PEBase-Freischaltung. */
export function buildCoachAssignmentMailHtml(data: CoachAssignmentMailData): string {
  const coach = escapeHtml(data.assignedCoachName || 'dein Betreuer');
  const salon = escapeHtml(data.salonName || 'dein Salon');
  const nr = escapeHtml(data.finalMandantennummer || '—');
  const pebase = escapeHtml(data.pebaseLink || DEFAULT_PEBASE);
  const vimeo = escapeHtml(data.vimeoLink || DEFAULT_VIMEO);
  const greeting = escapeHtml(formatHalloGreeting(firstNameFromClientName(data.clientName)));

  return `<!DOCTYPE html>
<html lang="de">
<head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/></head>
<body style="margin:0;padding:0;background:${DEEP};font-family:Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${DEEP};padding:32px 12px;">
    <tr>
      <td align="center">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background:${CARD};border:1px solid ${SLATE};border-radius:16px;overflow:hidden;">
          <tr>
            <td style="padding:28px 28px 8px 28px;">
              <p style="margin:0 0 6px 0;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;color:${GOLD};font-weight:700;">PreisExpert</p>
              <h1 style="margin:0 0 20px 0;font-size:22px;line-height:1.3;color:${TEXT};font-weight:800;">Dein persönlicher Betreuer &amp; PEBase-Zugang</h1>
              <p style="margin:0 0 14px 0;font-size:16px;line-height:1.55;color:${TEXT};">${greeting}</p>
              <p style="margin:0 0 14px 0;font-size:15px;line-height:1.6;color:${MUTED};">
                schön, dass du dabei bist. Für <strong style="color:${TEXT};">${salon}</strong> ist ab sofort
                <strong style="color:${GOLD};">${coach}</strong> dein persönlicher Ansprechpartner.
                Alles rund um Kalkulation, Freischaltung und PEBase läuft über diesen Kanal.
              </p>
            </td>
          </tr>

          <tr>
            <td style="padding:8px 28px 20px 28px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${SLATE};border:2px solid ${GOLD};border-radius:12px;">
                <tr>
                  <td style="padding:20px 18px;text-align:center;">
                    <p style="margin:0 0 10px 0;font-size:12px;letter-spacing:0.08em;text-transform:uppercase;color:${GOLD};font-weight:700;">
                      Deine persönliche Mandant-Nummer für das System:
                    </p>
                    <p style="margin:0 0 12px 0;font-size:26px;line-height:1.2;font-weight:800;color:${TEXT};letter-spacing:0.04em;">
                      [ ${nr} ]
                    </p>
                    <p style="margin:0;font-size:13px;line-height:1.5;color:${MUTED};">
                      Bitte exakt so kopieren – inklusive Bindestrich und Endkürzel.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding:4px 28px 8px 28px;">
              <p style="margin:0 0 16px 0;font-size:14px;letter-spacing:0.12em;text-transform:uppercase;color:${GOLD};font-weight:800;">
                3 Schritte zur Freischaltung
              </p>

              <p style="margin:0 0 6px 0;font-size:15px;font-weight:700;color:${TEXT};">Schritt 1 · Erklärvideo ansehen (ca. 5 Min.)</p>
              <p style="margin:0 0 14px 0;font-size:14px;line-height:1.55;color:${MUTED};">
                Kurz und klar: Installation und Einstieg.
              </p>
              <p style="margin:0 0 22px 0;">
                <a href="${vimeo}" style="display:inline-block;background:${GOLD};color:#0A0A0A;text-decoration:none;font-weight:800;font-size:13px;letter-spacing:0.06em;text-transform:uppercase;padding:14px 18px;border-radius:10px;">
                  ▶ Installations- &amp; Einführungsvideo auf Vimeo
                </a>
              </p>

              <p style="margin:0 0 6px 0;font-size:15px;font-weight:700;color:${TEXT};">Schritt 2 · PEBase aufrufen</p>
              <p style="margin:0 0 14px 0;font-size:14px;line-height:1.55;color:${MUTED};">
                Hier liegt dein interner Club und der direkte Draht zu uns.
              </p>
              <p style="margin:0 0 22px 0;">
                <a href="${pebase}" style="display:inline-block;background:${GOLD};color:#0A0A0A;text-decoration:none;font-weight:800;font-size:13px;letter-spacing:0.06em;text-transform:uppercase;padding:14px 18px;border-radius:10px;">
                  Zu PEBase wechseln ❯
                </a>
              </p>

              <p style="margin:0 0 6px 0;font-size:15px;font-weight:700;color:${TEXT};">Schritt 3 · Mandant-Nummer eingeben</p>
              <p style="margin:0 0 8px 0;font-size:14px;line-height:1.6;color:${MUTED};">
                Nach der Registrierung mit Deiner Mailadresse schaltet die Eingabe Deiner Mandant-Nummer
                den internen Club und Deinen direkten Draht zu uns sofort frei.
              </p>
            </td>
          </tr>

          <tr>
            <td style="padding:20px 28px 28px 28px;border-top:1px solid ${SLATE};">
              <p style="margin:0;font-size:13px;line-height:1.55;color:${MUTED};">
                Herzliche Grüße<br/>
                <strong style="color:${TEXT};">${coach}</strong><br/>
                PreisExpert · Friseur Coaching
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

export function buildCoachAssignmentMailText(data: CoachAssignmentMailData): string {
  const greeting = formatHalloGreeting(firstNameFromClientName(data.clientName));
  const coach = data.assignedCoachName || 'dein Betreuer';
  const salon = data.salonName || 'dein Salon';
  const nr = data.finalMandantennummer || '—';
  const pebase = data.pebaseLink || DEFAULT_PEBASE;
  const vimeo = data.vimeoLink || DEFAULT_VIMEO;

  return [
    greeting,
    '',
    `Für ${salon} ist ab sofort ${coach} dein persönlicher Ansprechpartner.`,
    '',
    'Deine persönliche Mandant-Nummer für das System:',
    nr,
    'Bitte exakt so kopieren – inklusive Bindestrich und Endkürzel.',
    '',
    '3 Schritte zur Freischaltung:',
    `1) Erklärvideo (ca. 5 Min.): ${vimeo}`,
    `2) PEBase aufrufen: ${pebase}`,
    '3) Nach der Registrierung mit Deiner Mailadresse schaltet die Eingabe Deiner Mandant-Nummer den internen Club und Deinen direkten Draht zu uns sofort frei.',
    '',
    'Herzliche Grüße',
    coach,
    'PreisExpert · Friseur Coaching',
  ].join('\n');
}

export function buildCoachAssignmentMailSubject(assignedCoachName: string): string {
  const coach = String(assignedCoachName || 'dein Betreuer').trim();
  return `Dein persönlicher Betreuer & Zugang zu PEBase: ${coach}`;
}
